import {createTheme, useTheme} from "@mui/material";
import {ThemeProvider} from "@mui/system";
import {useFooterColorMode, usePaperBackgroundContext} from "@nitra/nkmb-card-components";
import {PropsWithChildren, useEffect} from "react";


export function BlackPageContainer(props: PropsWithChildren<{
	/**
	 * Use this to override the default #080814 black
	 */
	blackOverride?: string;
}>) {
	const theme = useTheme();
	const {setPaperBackgroundColor} = usePaperBackgroundContext();
	const {setIsMonochrome} = useFooterColorMode()

	const blackTheme = createTheme(theme, {
		palette: {
			background: {
				default: props.blackOverride ?? "#080814",
				paper: "#080814"
			},
			text: {
				primary: "#FFFFFF",
				secondary: "#AAAAAA",
				disabled: "#666666",
			},
			primary: {
				main: "#FFFFFF",
				dark: "#CCCCCC",
				light: "#EEEEEE",
				contrastText: "#080814",
			},
			secondary: {
				main: "#FF0000",
				dark: "#CC0000",
				light: "#FF3333",
				contrastText: "#FFFFFF",
			},
			error: {
				main: "#FF4444",
				dark: "#CC0000",
				light: "#FF6666",
				contrastText: "#FFFFFF",
			},
			action: {
				active: "#FFFFFF",
				hover: "rgba(255, 255, 255, 0.1)",
				selected: "rgba(255, 255, 255, 0.2)",
				disabled: "rgba(255, 255, 255, 0.3)",
				disabledBackground: "rgba(255, 255, 255, 0.12)",
			},
			divider: "rgba(255, 255, 255, 0.12)",
		},
		components: {
			MuiButton: {
				styleOverrides: {
					root: {
						borderRadius: 4,
						textTransform: "none",
						fontWeight: 500,
					},
					contained: {
						boxShadow: "none",
						"&:hover": {
							boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
						},
					},
					outlined: {
						borderColor: "rgba(255, 255, 255, 0.5)",
						"&:hover": {
							borderColor: "#FFFFFF",
							backgroundColor: "rgba(255, 255, 255, 0.05)",
						},
					},
					text: {
						"&:hover": {
							backgroundColor: "rgba(255, 255, 255, 0.05)",
						},
					},
				},
			},
			MuiTextField: {
				styleOverrides: {
					root: {
						"& .MuiOutlinedInput-root": {
							backgroundColor: "rgba(255, 255, 255, 0.05)",
							"& fieldset": {
								borderColor: "rgba(255, 255, 255, 0.3)",
							},
							"&:hover fieldset": {
								borderColor: "rgba(255, 255, 255, 0.5)",
							},
							"&.Mui-focused fieldset": {
								borderColor: "#FFFFFF",
							},
							"&.Mui-error fieldset": {
								borderColor: "#FF4444",
							},
						},
						"& .MuiInputLabel-root": {
							color: "rgba(255, 255, 255, 0.7)",
							"&.Mui-focused": {
								color: "#FFFFFF",
							},
							"&.Mui-error": {
								color: "#FF4444",
							},
						},
						"& .MuiInputBase-input": {
							color: "#FFFFFF",
							"&::placeholder": {
								color: "rgba(255, 255, 255, 0.5)",
								opacity: 1,
							},
						},
						"& .MuiFormHelperText-root": {
							color: "rgba(255, 255, 255, 0.7)",
							"&.Mui-error": {
								color: "#FF4444",
							},
						},
					},
				},
			},
			MuiDialog: {
				styleOverrides: {
					paper: {
						backgroundColor: "#080814",
						backgroundImage: "none",
						boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.5)",
					},
				},
			},
			MuiDialogTitle: {
				styleOverrides: {
					root: {
						fontSize: "1.25rem",
						fontWeight: 600,
					},
				},
			},
			MuiDialogContent: {
				styleOverrides: {
					root: {
						padding: "16px 24px",
					},
				},
			},
			MuiDialogActions: {
				styleOverrides: {
					root: {
						padding: "16px 24px",
					},
				},
			},
			MuiDivider: {
				styleOverrides: {
					root: {
						backgroundColor: "rgba(255, 255, 255, 0.12)",
					},
				},
			},
			MuiTypography: {
				styleOverrides: {
					root: {
						color: "#FFFFFF",
					},
				},
			},
		},
	})


	useEffect(() => {
		const black = props.blackOverride ?? "#080814"
		document.body.style.backgroundColor = black;
		setPaperBackgroundColor(black);
		setIsMonochrome(true);
		return () => {
			document.body.style.backgroundColor = theme.palette.background.paper;
			setPaperBackgroundColor(theme.palette.background.paper);
			setIsMonochrome(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])


	return (
		<ThemeProvider theme={blackTheme}>
			{props.children}
		</ThemeProvider>
	)
}
